import { Controller } from '@hotwired/stimulus';

/**
 * A reusable toggle controller that can be used to show/hide elements
 *
 * Targets:
 * - content: The element to toggle
 * - showButton: Optional button to show the content
 * - hideButton: Optional button to hide the content
 *
 * Classes:
 * - hiddenClass: The class to add/remove for hiding content (default: "hidden")
 *
 * Usage:
 * <div data-controller="toggle" data-toggle-hidden-class="hidden">
 *   <button data-toggle-target="showButton" data-action="toggle#toggle">Show</button>
 *   <button data-toggle-target="hideButton" data-action="toggle#toggle" class="hidden">Hide</button>
 *   <div data-toggle-target="content" class="hidden">Content to toggle</div>
 * </div>
 */
export default class extends Controller {
  static targets = ['content', 'showButton', 'hideButton'];
  static values = {
    hidden: { type: Boolean, default: true },
    hiddenClass: { type: String, default: 'hidden' },
  };

  connect() {
    this.updateVisibility();
  }

  toggle() {
    this.hiddenValue = !this.hiddenValue;
    this.updateVisibility();
  }

  show() {
    this.hiddenValue = false;
    this.updateVisibility();
  }

  hide() {
    this.hiddenValue = true;
    this.updateVisibility();
  }

  updateVisibility() {
    this.contentTargets.forEach((element) => {
      if (this.hiddenValue) {
        element.classList.add(this.hiddenClassValue);
      } else {
        element.classList.remove(this.hiddenClassValue);
      }
    });

    if (this.hasShowButtonTarget && this.hasHideButtonTarget) {
      this.showButtonTargets.forEach((button) => {
        if (this.hiddenValue) {
          button.classList.remove(this.hiddenClassValue);
        } else {
          button.classList.add(this.hiddenClassValue);
        }
      });

      this.hideButtonTargets.forEach((button) => {
        if (this.hiddenValue) {
          button.classList.add(this.hiddenClassValue);
        } else {
          button.classList.remove(this.hiddenClassValue);
        }
      });
    }
  }
}
